import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useUiStore, toDateStr } from '../../lib/uiStore';
import { useLang, localName } from '../../lib/lang';
import { hmsToMinutes, isoForDateMinutes, localMinutesOfDay, minutesToLabel } from '../../lib/time';
import { layout, SLOT_PX, HEADER_PX } from './layout';
import { SessionHoverCard, useHoverCard, useTagResolver } from './SessionHoverCard';
import type { Session } from '../../lib/types';

export function WeekView({ branchId, date }: { branchId: number; date: string }): JSX.Element {
  const openCreate = useUiStore((s) => s.openCreate);
  const openEdit = useUiStore((s) => s.openEdit);
  const setDate = useUiStore((s) => s.setDate);
  const setView = useUiStore((s) => s.setView);
  const { t, lang } = useLang();
  const locale = lang === 'ar' ? 'ar' : 'en-US';
  const weekdayShort = (d: Date): string => new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d);

  const anchor = new Date(`${date}T00:00:00`);
  const rangeFrom = new Date(anchor); rangeFrom.setDate(rangeFrom.getDate() - 7);
  const rangeTo = new Date(anchor); rangeTo.setDate(rangeTo.getDate() + 8);

  const q = useQuery({
    queryKey: ['calendar-week', branchId, date],
    queryFn: () => api.calendar(branchId, { from: rangeFrom.toISOString(), to: rangeTo.toISOString() }),
  });

  const empName = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of q.data?.resources ?? []) m.set(r.id, r.name);
    return m;
  }, [q.data]);

  const byDay = useMemo(() => {
    const m = new Map<string, Session[]>();
    for (const s of q.data?.sessions ?? []) {
      const key = toDateStr(new Date(s.start_at));
      const arr = m.get(key) ?? [];
      arr.push(s);
      m.set(key, arr);
    }
    return m;
  }, [q.data]);

  const hover = useHoverCard(false);
  const tagsQ = useQuery({ queryKey: ['tags'], queryFn: api.tags });
  const tagsOf = useTagResolver(tagsQ.data, lang);

  if (q.isLoading) return <div className="flex h-full items-center justify-center text-sm text-slate-400">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>;
  if (q.isError || !q.data) return <div className="flex h-full items-center justify-center text-sm text-red-600">{t({ en: 'Couldn’t load the week.', ar: 'تعذّر تحميل الأسبوع.' })}</div>;

  const settings = q.data.settings;
  const statusById = new Map((q.data.statuses ?? []).map((s) => [s.id, s]));
  const statusLabel = (session: Session): string => {
    const st = statusById.get(session.status_id);
    return localName(lang, st?.name ?? session.status_name, st?.name_ar);
  };
  const dayStart = hmsToMinutes(settings.day_start_time || '09:00');
  let dayEnd = hmsToMinutes(settings.day_end_time || '21:00');
  if (dayEnd <= dayStart) dayEnd = dayStart + 12 * 60;
  const slot = Math.max(5, settings.slot_duration_minutes || 30);
  const snap = Math.max(1, settings.slot_snap_minutes || 15);
  const pxPerMin = SLOT_PX / slot;
  const bodyHeight = (dayEnd - dayStart) * pxPerMin;

  const rows: number[] = [];
  for (let m = dayStart; m <= dayEnd; m += slot) rows.push(m);

  const fdow = settings.first_day_of_week;
  const weekStart = new Date(anchor);
  weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() - fdow + 7) % 7));
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const todayStr = toDateStr(new Date());

  const clickEmpty = (e: React.MouseEvent<HTMLDivElement>, dayStr: string): void => {
    if ((e.target as HTMLElement).closest('[data-session]')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    let minutes = dayStart + Math.round((e.clientY - rect.top) / pxPerMin / snap) * snap;
    minutes = Math.max(dayStart, Math.min(dayEnd - slot, minutes));
    openCreate({ employeeId: null, start: isoForDateMinutes(dayStr, minutes) });
  };

  return (
    <div dir="ltr" className="appt-scroll h-full overflow-auto bg-slate-100 dark:bg-slate-950">
      <div className="flex min-w-full">
        {/* Time gutter */}
        <div className="sticky left-0 z-10 flex-none bg-slate-100 dark:bg-slate-950" style={{ width: 'var(--appt-gutter)' }}>
          <div className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-950" style={{ height: HEADER_PX }} />
          <div className="relative" style={{ height: bodyHeight }}>
            {rows.map((m) => (
              <div key={m} className="absolute pr-2 pt-0.5 text-right text-[11px] font-medium text-slate-400" style={{ top: (m - dayStart) * pxPerMin, right: 0, left: 0 }}>
                {minutesToLabel(m)}
              </div>
            ))}
          </div>
        </div>

        {/* Day columns */}
        <div className="flex flex-1">
          {days.map((d) => {
            const ds = toDateStr(d);
            const laid = layout(byDay.get(ds) ?? [], dayStart, pxPerMin);
            const isToday = ds === todayStr;
            return (
              <div
                key={ds}
                className="flex-1 border-s border-slate-200 dark:border-slate-800"
                style={{ minWidth: 'var(--appt-week-col-min)' }}
              >
                <button
                  onClick={() => { setDate(ds); setView('day'); }}
                  className={`sticky top-0 z-10 flex w-full flex-col items-center justify-center border-b border-slate-200 dark:border-slate-800 ${isToday ? 'bg-brand/10' : 'bg-white dark:bg-slate-900'}`}
                  style={{ height: HEADER_PX }}
                  title={t({ en: 'Open day view', ar: 'فتح عرض اليوم' })}
                >
                  <span className="text-[10px] font-semibold uppercase text-slate-400">{weekdayShort(d)}</span>
                  <span className={`text-sm font-bold ${isToday ? 'flex h-6 w-6 items-center justify-center rounded-full bg-brand text-white' : 'text-slate-700 dark:text-slate-200'}`}>{d.getDate()}</span>
                </button>
                <div
                  className="relative cursor-pointer bg-white transition hover:bg-slate-50/50 dark:bg-slate-900 dark:hover:bg-slate-800/40"
                  style={{
                    height: bodyHeight,
                    backgroundImage: 'repeating-linear-gradient(to bottom, rgba(148,163,184,0.18) 0, rgba(148,163,184,0.18) 1px, transparent 1px, transparent ' + SLOT_PX + 'px)',
                  }}
                  onClick={(e) => clickEmpty(e, ds)}
                >
                  {laid.map(({ session, top, height, lane, lanes }) => {
                    const color = session.color ?? session.status_color ?? '#3B82F6';
                    const widthPct = 100 / lanes;
                    const emp = session.employee_id != null ? empName.get(session.employee_id) : null;
                    return (
                      <button
                        key={session.id}
                        data-session
                        onClick={() => openEdit(session.id)}
                        onPointerEnter={(e) => {
                          if (e.pointerType !== 'mouse') return;
                          hover.open(e.currentTarget, () => ({
                            session,
                            statusLabel: statusLabel(session),
                            statusColor: color,
                            employeeName: emp,
                            tags: tagsOf(session),
                          }));
                        }}
                        onPointerLeave={hover.close}
                        className="absolute overflow-hidden rounded-md px-1.5 py-0.5 text-start shadow-sm ring-1 ring-black/5 transition hover:z-10 hover:shadow-md"
                        style={{ top: top + 1, height, left: `calc(${lane * widthPct}% + 1px)`, width: `calc(${widthPct}% - 2px)`, background: `${color}1a`, borderInlineStart: `3px solid ${color}` }}
                      >
                        <div className="truncate text-[11px] font-semibold text-slate-800 dark:text-slate-100">{session.customer_name ?? t({ en: 'Walk-in', ar: 'زائر' })}</div>
                        {height >= 34 && (
                          <div className="truncate text-[10px] text-slate-500 dark:text-slate-400">
                            {minutesToLabel(localMinutesOfDay(session.start_at))}{emp ? ` · ${emp}` : ''}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {hover.target && <SessionHoverCard target={hover.target} />}
    </div>
  );
}
